<template>
	<main>
		<HeaderTab :title="$t('global.titre_mare_liste')">
			<template #actions>
				<div class="d-flex align-items-center" style="gap: 0.5rem">
					<!-- Recherche + filtre propriétaire : desktop uniquement (md+) -->
					<div class="search_mare d-none d-md-flex">
						<b-input v-model="search" :placeholder="$t('monte.rechercher_mare')" size="sm"/>
						<font-awesome-icon :icon="['fal', 'search']"/>
					</div>
					<b-form-select
						v-if="has_multiple_tiers"
						v-model="tiers_selected"
						:options="all_tiers_options"
						size="sm"
						style="width: auto"
						class="d-none d-md-block"
					/>
					<b-button @click="goToAddHorse" variant="primary" size="sm">{{ $t('monte.add_jument') }} <font-awesome-icon :icon="['fal', 'plus']"/></b-button>
				</div>
			</template>
		</HeaderTab>

		<div id="content">
			<div class="container-fluid">
				<!-- Recherche + filtre propriétaire : mobile uniquement -->
				<div v-if="items && items.length > 0" class="d-flex d-md-none flex-wrap align-items-center pt-2 pb-1" style="gap: 0.5rem">
					<div class="search_mare flex-grow-1">
						<b-input v-model="search" :placeholder="$t('monte.rechercher_mare')" size="sm"/>
						<font-awesome-icon :icon="['fal', 'search']"/>
					</div>
					<b-form-select
						v-if="has_multiple_tiers"
						v-model="tiers_selected"
						:options="all_tiers_options"
						size="sm"
						style="width: auto"
					/>
				</div>
				<div class="row">
					<div class="col-12">
						<div id="marelist">
							<div v-if="table_busy">
								<LoadingSpinner />
							</div>
							<div v-else class="row">
								<div v-if="filtred_horses.length === 0" class="col-12">
									<EmptyState :label="$t('global.empty_state_default')">
										<template #action>
											<b-button @click="goToAddHorse" variant="primary" size="sm">{{ $t('monte.add_jument') }} <font-awesome-icon :icon="['fal', 'plus']"/></b-button>
										</template>
									</EmptyState>
								</div>
								<div v-for="horse in filtred_horses" class="col-12 col-md-6 col-xl-4 mb-2 mb-lg-3" :key="horse.horse_id">
									<div class="mares">
										<div class="w-100" @click="goToHorse(horse.horse_id)">
											<div class="row align-items-center mb-1">
												<div class="col">
													<b><a href="#" @click.prevent>{{ horse.horse_nom }}</a></b>
												</div>
												<div class="col-auto d-flex align-items-center" style="gap: 0.4rem">
													<b-badge v-if="hasPaxAccess && pax_percents[horse.horse_id] !== undefined" pill variant="primary" :title="$t('horse.caracterisation_pax')">PAX {{ pax_percents[horse.horse_id] }}%</b-badge>
													<a href="#" @click.stop.prevent="deleteHorse(horse)" class="icone"><font-awesome-icon :icon="['fal', 'trash-alt']"/></a>
												</div>
											</div>
											<div class="text-muted small">
												{{ horse.race_label }}<template v-if="horse.robe_label"> · {{ horse.robe_label }}</template><template v-if="horse.horse_yearnaissance"> · {{ $t('horse.born') }} {{ horse.horse_yearnaissance }}</template>
											</div>
											<div v-if="horse.pedigree && horse.pedigree.pere" class="text-muted small">
												{{ horse.pedigree.pere }} × {{ horse.pedigree.mere }}
											</div>
											<!-- Propriétaire affiché uniquement si plusieurs tiers distincts sur l'ensemble des juments -->
											<div v-if="has_multiple_tiers && horse.tiers && horse.tiers.length" class="mt-2 pt-2 border-top small text-muted">
												<font-awesome-icon :icon="['fal', 'user']" class="mr-1"/>{{ horse.tiers.map(t => t.tiers_rs).join(', ') }}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<b-modal ref="confirmDelete" hide-footer centered>
			<template v-slot:modal-title>
				<font-awesome-icon :icon="['fal', 'horse']" class="text-primary mr-2" />
				{{ $t('horse.confirm_supprimer_jument') }}
			</template>

			<div v-if="horse_to_delete" class="mb-3">
				<div class="row align-items-center">
					<div class="col">
						<h5 class="mb-0">{{ horse_to_delete.horse_nom }}</h5>
						<div v-if="horse_to_delete.pedigree.pere">{{ horse_to_delete.pedigree.pere }} × {{ horse_to_delete.pedigree.mere }}</div>
					</div>
				</div>
			</div>

			<div class="d-flex justify-content-between">
				<b-button variant="secondary" @click="$refs.confirmDelete.hide()">{{ $t('global.annuler') }}</b-button>
				<b-button variant="primary" @click="confirmDeleteHorse">{{ $t('horse.supprimer_jument') }} <font-awesome-icon :icon="['fal', 'trash-alt']" /></b-button>
			</div>
		</b-modal>
	</main>
</template>

<script type="text/javascript">

import Horse from '@/mixins/Horse.js'
import ActesMixin from "@/mixins/Actes.js";
import Navigation from "@/mixins/Navigation.js";
import PaxMixin from "@/mixins/Pax.js";
import _cloneDeep from 'lodash/cloneDeep';

export default {
	name: "MareListeV2",
	mixins: [Horse, ActesMixin, Navigation, PaxMixin],
	data () {
		return {
			items: null,
			table_busy: true,
			search: '',
			tiers_selected: null,
			horse_to_delete: null,
			pax_percents: {}
		}
	},
	computed: {
		hasSaillieAccess() {
			return this.$store.state.userAccess.hasSaillieAccess
		},

		hasPaxAccess() {
			return this.$store.state.userAccess.hasPaxAccess
		},

		// Ensemble des tiers distincts présents sur toutes les juments
		all_tiers() {
			if (!this.items) return []
			const map = new Map()
			this.items.forEach(h => (h.tiers || []).forEach(t => map.set(t.tiers_id, t)))
			return [...map.values()]
		},

		// Vrai si au moins deux propriétaires différents — affiche alors le filtre et les noms sur les cards
		has_multiple_tiers() {
			return this.all_tiers.length > 1
		},

		// Options du sélecteur de propriétaire pour b-form-select
		all_tiers_options() {
			const all = { value: null, text: this.$t('monte.tous_proprietaires') }
			return [all, ...this.all_tiers.map(t => ({ value: t.tiers_id, text: t.tiers_rs }))]
		},

		// Liste filtrée par recherche textuelle et/ou propriétaire sélectionné
		filtred_horses() {
			if (!this.items) return []
			let horses = this.items
			if (this.search) {
				const s = this.search.toUpperCase()
				horses = horses.filter(h => h.horse_nom.toUpperCase().includes(s))
			}
			if (this.tiers_selected !== null) {
				horses = horses.filter(h => h.tiers && h.tiers.some(t => t.tiers_id === this.tiers_selected))
			}
			return horses.slice().sort((a, b) => a.horse_nom.localeCompare(b.horse_nom, undefined, { sensitivity: 'base' }))
		}
	},
	mounted () {
		this.init_component()
	},
	watch: {
		hasPaxAccess(val) {
			if (val && !this.table_busy) {
				this.loadPaxPercents()
			}
		}
	},
	methods: {
		async init_component() {
			this.getHorses('F').then(horse_api => {
				this.items = this.formatDataHorse(horse_api)
				this.table_busy = false
				if (this.hasPaxAccess) {
					this.loadPaxPercents()
				}
			})
		},

		loadPaxPercents() {
			this.getCaracterisationPercents().then(percents => {
				if (percents) {
					this.pax_percents = percents
				}
			})
		},

		deleteHorse(horse) {
			this.horse_to_delete = horse
			this.$refs.confirmDelete.show()
		},

		async confirmDeleteHorse() {
			this.$refs.confirmDelete.hide()
			await this.hideHorseFromCA({horse_ids: this.horse_to_delete.horse_id})
			this.horse_to_delete = null
			this.init_component()
		},

		goToAddHorse() {
			this.$router.push({ name: 'horseAjout', query: { from: 'mare' } })
		},

		goToHorse(horse_id) {
			this.$router.push({ name: 'horseFiche', params: {horse_id} })
		}
	},
	components: {
		HeaderTab: () => import('@/components/HeaderTab'),
		LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		EmptyState: () => import('@/components/EmptyState')
	}
}
</script>